import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { OrganizerSection } from './full/OrganizerSection';
import { Trans } from 'react-i18next';
import { ActivityEventIcon } from './ActivityEventIcon';
import { showDialog } from '@src/components/Dialog/Dialog';
import { getCurrencySymbol } from '@src/site/currency';
import { presentActivity, type Activity, type Value, type IdentitySnapshot } from './activityPresentation';
import { Pagination } from '@src/components/Pagination/Pagination';
import { UserIcon } from '@src/components/icons/UserIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { ActivityPicture } from './ActivityPicture';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Button } from '@src/components/Button/Button';
import { useMobileBoxLayout } from './full/BoxLayouts';
import './BoxActivity.css';

type ActivityPage = { viewerCardId?: number | null; events: Activity[]; total: number; pageSize: number; preview: boolean; pictures?: Record<string, string> };

export function BoxActivity({ info, cards }: { info: BoxInfo; cards: CardInfo[] }) {
  const { t, i18n } = useTranslation();
  const mobile = useMobileBoxLayout();
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set());
  const [page, setPage] = useState(1);
  const [expandedChanges, setExpandedChanges] = useState<Set<number>>(() => new Set());
  const [result, setResult] = useState<{ request: string; data: ActivityPage } | null>(null);
  const [failedRequest, setFailedRequest] = useState('');
  const [retry, setRetry] = useState(0);
  const { key } = info.box;
  const trackedPage = useRef<string | null>(null);
  const request = `${key}:${page}:${retry}`;
  const data = result?.request === request ? result.data : null;
  const error = failedRequest === request;
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    fetch(`/api/box/${encodeURIComponent(key)}/events?page=${page}`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Activity request failed');
        const result: ActivityPage = await response.json();
        if (!controller.signal.aborted) {
          setResult({ request, data: result });
          setFailedRequest('');
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailedRequest(request);
      });
    return () => controller.abort();
  }, [key, page, open, request, cards, info]);
  function recordUsage(action: 'page_viewed' | 'details_viewed', eventId?: number) {
    if (!open || !data || data.preview) return;
    void fetch(`/api/box/${encodeURIComponent(key)}/events/usage`, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, page, ...(eventId === undefined ? {} : { eventId }) }),
    }).catch(() => { /* Usage reporting must not interrupt viewing activity. */ });
  }
  useEffect(() => {
    if (!open) { trackedPage.current = null; return; }
    if (!data || data.preview || !data.events.length || (page === 1 && data.events.length <= 3)) return;
    if (trackedPage.current === request) return;
    trackedPage.current = request;
    void fetch(`/api/box/${encodeURIComponent(key)}/events/usage`, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'page_viewed', page }),
    }).catch(() => { /* Best effort; do not retry and inflate the usage history. */ });
  }, [open, data, request, key, page]);
  const value = (v: Value) =>
    v == null || v === ''
      ? '—'
      : typeof v === 'boolean'
        ? t(v ? 'boxActivity.yes' : 'boxActivity.no')
        : String(v);
  const changeValue = (field: string, v: Value, theme?: string, avatar?: string | null) => {
    if (field === 'participantPictureId') {
      const picture = data?.pictures?.[String(v)];
      return <span className="box-activity__pairing-avatar">
        {picture ? <ParticipantFace picture={picture} avatar={avatar} /> : <UserIcon />}
      </span>;
    }
    if (field === 'budgetAmount' && typeof v === 'number') return new Intl.NumberFormat(i18n.language).format(v);
    if ((field === 'pictureId' || field === 'logo') && v) {
      const picture = field === 'pictureId' ? data?.pictures?.[String(v)] : undefined;
      if (field === 'pictureId' && !picture) return '—';
      return (
        <span
          className="box-activity__picture"
          role="img"
          aria-label={t('boxActivity.fields.pictureId')}
        >
          <ActivityPicture
            picture={picture}
            logo={field === 'logo' ? String(v) : undefined}
            theme={theme}
          />
        </span>
      );
    }
    if ((field === 'mode' || field === 'theme') && typeof v === 'string')
      return t(`boxActivity.values.${v}`, { defaultValue: v });
    return value(v);
  };
  const creationBudget = (settings: Record<string, Value>) => {
    if (!settings.useCashLimit) return t('boxActivity.no');
    const amount = (v: Value | undefined) =>
      typeof v === 'number' ? new Intl.NumberFormat(i18n.language).format(v) : '—';
    const currency =
      typeof settings.cashLimitCurrency === 'string'
        ? (getCurrencySymbol(settings.cashLimitCurrency) || settings.cashLimitCurrency.toUpperCase())
        : '';
    return `${amount(settings.cashLimitMin)} - ${amount(settings.cashLimit)} ${currency}`.trim();
  };
  const participantFace = (event: Activity) => {
    const card = cards.find((candidate) => candidate.id === event.cardId);
    const picture = event.metadata?.participant ? event.participantPicture : (event.participantPicture || card?.picture);
    return picture ? (
      <ParticipantFace frameName={card?.frameName} picture={picture} avatar={event.metadata?.participant ? event.participantAvatar : (event.participantAvatar ?? card?.avatar)} />
    ) : (
      <ThemeIcon name="user" fallback={<UserIcon />} />
    );
  };
  const pairingName = (id: number, name: string, snapshot?: IdentitySnapshot) => (
    <span className="box-activity__pairing-person">
      <span className="box-activity__pairing-avatar" aria-hidden="true">
        {snapshot ? <ParticipantFace frameName={cards.find(card => card.id === id)?.frameName} picture={data?.pictures?.[String(snapshot.pictureId)] || 'question'} avatar={snapshot.avatar} /> : participantFace({ cardId: id } as Activity)}
      </span>
      <b>{snapshot?.name || name || t('boxActivity.cardNumber', { id })}</b>
    </span>
  );
  const content = (
    <div className="box-activity__body" aria-live="polite">
      <div className="box-activity__intro">
        <p>{t('boxActivity.description')}</p>
      </div>
      {error ? (
        <>
          <p>{t('boxActivity.error')}</p>
          <Button onClick={() => setRetry((n) => n + 1)}>{t('boxActivity.retry')}</Button>
        </>
      ) : !data ? (
        <p>{t('boxActivity.loading')}</p>
      ) : (
        <>
          {!data.events.length && <p>{t('boxActivity.empty')}</p>}
          <ol className="box-activity__list">
            {data.events.map((event) => {
              const presentation = presentActivity(event, t, changeValue, (name, role) => {
                const receiver = role === 'receiver';
                const snapshot = receiver ? event.metadata.receiver : event.metadata.participant;
                const identityEvent = receiver ? {
                  cardId: event.metadata.receiverCardId,
                  participantPicture: snapshot ? data.pictures?.[String(snapshot.pictureId)] : undefined,
                  participantAvatar: snapshot?.avatar,
                  metadata: { participant: snapshot },
                } as Activity : event;
                return <span className="box-activity__person">
                  <span className="box-activity__pairing-avatar" aria-hidden="true">{participantFace(identityEvent)}</span>
                  <b>{name}</b>
                </span>;
              }, data.viewerCardId, pairingName);
              const hasCreationSettings = event.type === 'box.created' && Boolean(event.metadata.settings) && event.metadata.settings?.mode !== 'quick';
              return (
              <li
                key={event.id}
                className={
                  event.type === 'box.created' &&
                  event.metadata.settings &&
                  event.metadata.settings.mode !== 'quick'
                    ? 'box-activity__creation'
                    : ['participant.card_updated', 'box.settings_updated', 'box.exclusions_updated'].includes(event.type) ? 'box-activity__card-update' : ['box.draw_completed', 'box.draw_updated', 'participant.giftee_revealed', 'participant.giftee_changed'].includes(event.type) ? 'box-activity__draw' : undefined
                }
              >
                <span className="box-activity__avatar" aria-hidden="true">
                  <ActivityEventIcon type={event.type} />
                </span>
                <div>
                  <strong className="box-activity__title-regular">{presentation.title}</strong>
                    {(hasCreationSettings || (presentation.description && ['participant.card_updated', 'box.settings_updated', 'box.exclusions_updated'].includes(event.type))) && (
                      <button type="button" className="box-activity__pairings-toggle box-activity__changes-toggle"
                        aria-expanded={expandedChanges.has(event.id)} aria-controls={`${hasCreationSettings ? 'activity-settings' : 'activity-changes'}-${event.id}`}
                        onClick={() => {
                          if (!expandedChanges.has(event.id)) recordUsage('details_viewed', event.id);
                          setExpandedChanges((current) => {
                          const next = new Set(current);
                          if (next.has(event.id)) next.delete(event.id); else next.add(event.id);
                          return next;
                        }); }}>
                        {t(mobile ? (expandedChanges.has(event.id) ? 'boxActivity.hideShort' : 'boxActivity.showShort') : hasCreationSettings ? (expandedChanges.has(event.id) ? 'boxActivity.hideDetails' : 'boxActivity.showDetails') : expandedChanges.has(event.id) ? 'boxActivity.hideChanges' : 'boxActivity.showChanges')}
                        <ChevronDownIcon />
                      </button>
                    )}
                  {presentation.description && (
                    <div className="box-activity__description" id={`activity-changes-${event.id}`}
                      hidden={['participant.card_updated', 'box.settings_updated', 'box.exclusions_updated'].includes(event.type) && !expandedChanges.has(event.id)}>
                      {presentation.description}
                    </div>
                  )}
                  <time dateTime={event.createdAt}>
                    {new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(event.createdAt))}
                  </time>
                  {['box.draw_completed', 'box.draw_updated', 'participant.giftee_revealed', 'participant.giftee_changed'].includes(event.type) && (
                    event.metadata.assignments?.length ? (
                      <>
                        <button type="button" className="box-activity__pairings-toggle"
                          aria-expanded={revealed.has(event.id)} aria-controls={`draw-pairs-${event.id}`}
                          onClick={() => {
                            if (revealed.has(event.id)) {
                              setRevealed((current) => {
                                const next = new Set(current);
                                next.delete(event.id);
                                return next;
                              });
                              return;
                            }
                            if (event.type === 'box.draw_updated' || (event.type === 'participant.giftee_changed' && event.metadata.reason === 'manual')) {
                              recordUsage('details_viewed', event.id);
                              setRevealed((current) => new Set(current).add(event.id));
                              return;
                            }
                            showDialog({
                              title: t(event.type === 'participant.giftee_revealed' ? 'boxActivity.revealGifteeConfirmTitle' : 'boxActivity.revealConfirmTitle'),
                              children: t(event.type === 'participant.giftee_revealed' ? 'boxActivity.revealGifteeConfirmBody' : 'boxActivity.revealConfirmBody'),
                              cancel: t('box.cancel'),
                              submit: t(event.type === 'participant.giftee_revealed' ? 'boxActivity.revealGiftee' : 'boxActivity.revealPairings'),
                              testId: 'draw-pairings-confirm',
                              onSubmit: () => { recordUsage('details_viewed', event.id); setRevealed((current) => new Set(current).add(event.id)); },
                            });
                          }}>
                          {t(mobile ? (revealed.has(event.id) ? 'boxActivity.hideShort' : 'boxActivity.showShort') : revealed.has(event.id) ? 'boxActivity.hidePairings' : event.type === 'participant.giftee_revealed' ? 'boxActivity.revealGiftee' : 'boxActivity.revealPairings')}
                          <ChevronDownIcon />
                        </button>
                        {event.metadata.additional && (
                          <div className="box-activity__draw-participants">
                            <span>{t('boxActivity.additionalDrawParticipants')}</span>
                            {event.metadata.assignments.map((pair) => <span key={pair.cardId}>
                              {pairingName(pair.cardId, pair.cardName, pair.giver)}
                            </span>)}
                          </div>
                        )}
                        <div id={`draw-pairs-${event.id}`} className="box-activity__pairings" hidden={!revealed.has(event.id)}>

                          <div className="box-activity__pairing-list">
                            {event.metadata.assignments.map((pair) => ['participant.giftee_changed', 'box.draw_updated'].includes(event.type) ? (
                              <div key={pair.cardId} className="box-activity__giftee-change">
                                {event.type === 'box.draw_updated' && <><strong>{pairingName(pair.cardId, pair.cardName, pair.giver)}</strong>{': '}</>}
                                <Trans t={t} i18nKey={pair.previousWardId ? 'boxActivity.gifteeChangedFromTo' : 'boxActivity.gifteeAssignedTo'} components={{
                                  previous: <del>{pair.previousWardId ? pairingName(pair.previousWardId, '', pair.previousRecipient) : t('boxActivity.noGifteeAssigned')}</del>,
                                  next: <span>{pair.wardId ? pairingName(pair.wardId, pair.wardName, pair.recipient) : t('boxActivity.noGifteeAssigned')}</span>,
                                }} />
                              </div>
                            ) : (
                              <p key={pair.cardId}>
                                {pairingName(pair.cardId, pair.cardName, pair.giver)}
                                <span>{t('boxActivity.givesTo')}</span>
                                {pair.wardId ? pairingName(pair.wardId, pair.wardName, pair.recipient) : <span>{t('boxActivity.noGifteeAssigned')}</span>}
                              </p>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : <p className="box-activity__pairings">{t(event.type === 'participant.giftee_revealed' ? 'boxActivity.gifteeSnapshotUnavailable' : 'boxActivity.pairingsUnavailable')}</p>
                  )}
                  {event.type === 'box.created'  && event.metadata.settings?.mode !== 'quick' && (
                    <>
                      {event.metadata.settings && (
                        <dl className="box-activity__initial-settings" id={`activity-settings-${event.id}`} hidden={!expandedChanges.has(event.id)}>
                          {Object.entries(event.metadata.settings)
                            .filter(
                              ([field]) =>
                                ![
                                  'year',
                                  'mode',
                                  'useCircleDraw',
                                  'isInviteAfterDraw',
                                  'useNames',
                                  'useCashLimit',
                                  'cashLimitMin',
                                  'cashLimit',
                                  'cashLimitCurrency',
                                ].includes(field),
                            )
                            .map(([field, setting]) => (
                              <div key={field}>
                                <dt>{t(`boxActivity.fields.${field}`, { defaultValue: field })}</dt>
                                <dd>{changeValue(field, setting)}</dd>
                              </div>
                            ))}
                          <div>
                            <dt>{t('boxActivity.budget')}</dt>
                            <dd>{creationBudget(event.metadata.settings)}</dd>
                          </div>
                        </dl>
                      )}
                    </>
                  )}
                  {presentation.changes.length > 0 && (
                    <dl className="box-activity__changes">
                      {presentation.changes
                        .map(([field, before]) => (
                          <div key={field}>
                            <dt>{t(`boxActivity.fields.${field}`, { defaultValue: field })}</dt>
                            <dd>
                              <b>{changeValue(
                                field,
                                before,
                                event.metadata.previousTheme || event.metadata.theme,
                              )}</b>{' '}
                              →{' '}
                              <b>{changeValue(
                                field,
                                event.metadata.after?.[field] ?? null,
                                event.metadata.theme,
                              )}</b>
                            </dd>
                          </div>
                        ))}
                    </dl>
                  )}
                </div>
              </li>
              );
            })}
          </ol>
          {data.preview && <PremiumUpsell feature="history" compact />}
          {!data.preview && data.total > data.pageSize && (
            <Pagination
              page={page}
              pageCount={Math.ceil(data.total / data.pageSize)}
              onChange={setPage}
              previousLabel={t('boxActivity.previous')}
              nextLabel={t('boxActivity.next')}
              pageLabel={(n) => t('boxActivity.page', { page: n })}
            />
          )}

        </>
      )}
    </div>
  );
  return (
    <OrganizerSection gameKey={info.box.key} mobile={mobile} id="history"
      icon={<ThemeIcon name="history" fallback={<ClockIcon />} />}
      title={t('boxActivity.title')}
      premium premiumFeature="history"
      onToggle={setOpen}>
      <div className="box-activity" data-testid="box-activity">{open && content}</div>
    </OrganizerSection>
  );
}
